import { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Unplug, LineChart } from 'lucide-react';
import { Device } from '@repo/shared/dtos/devices/DeviceConnectionDto';
import { ConfirmDialog } from './ConfirmDialog';

interface DeviceItemProps {
  device: Device;
  isDisconnecting: boolean;
  onDisconnect: (id: string) => void;
  onClick: (id: string) => void;
}

export const DeviceItem = memo(
  ({ device, isDisconnecting, onDisconnect, onClick }: DeviceItemProps) => {
    const [isHovering, setIsHovering] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleClick = () => {
      onClick(device.deviceid);
    };

    const handleDisconnect = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setShowConfirm(true);
    }, []);

    const confirmDisconnect = useCallback(() => {
      onDisconnect(device.deviceid);
      setShowConfirm(false);
    }, [device.deviceid, onDisconnect]);

    return (
      <>
        <motion.div
          className="border-b border-[var(--anl-border)] last:border-0"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          transition={{ duration: 0.2 }}
        >
          <div
            className={`flex items-center justify-between rounded px-2 py-3 transition-colors hover:bg-[var(--anl-surface-elev)] ${'cursor-pointer'}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={`Device: ${device.devicename}`}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick();
              }
            }}
          >
            <div className="flex flex-1 items-center">
              <div className="mr-3 flex-shrink-0">
                <div className="relative mr-2 h-2 w-2 rounded-full bg-green-400" aria-hidden="true">
                  <span className="absolute left-0 top-0 h-2 w-2 animate-ping rounded-full bg-green-400 opacity-75"></span>
                </div>
              </div>
              <div>
                <div className="flex items-center font-medium text-[var(--anl-text)]">
                  {device.devicename}
                  <span className="ml-3 rounded-full bg-[var(--anl-surface-elev)] px-1.5 py-0.5 text-xs text-[var(--anl-text)]">
                    Connected
                  </span>
                </div>
                {device.formattedCreateDate && (
                  <div className="mt-1 text-xs text-[var(--anl-text-muted)]">
                    Connected {device.formattedCreateDate}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-1 text-xs text-[var(--anl-accent)]">
                  <LineChart className="h-3.5 w-3.5" />
                  <span>Click to view analytics</span>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <button
                className={`mr-2 flex items-center gap-1 rounded px-2 py-1 text-[var(--anl-error)] transition-all ${
                  isHovering
                    ? 'opacity-100 hover:bg-[var(--anl-surface-elev)]'
                    : 'opacity-0 sm:invisible sm:opacity-100'
                }`}
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                aria-label={`Disconnect ${device.devicename}`}
              >
                <Unplug className="h-5 w-5" aria-hidden="true" />
                <span className="hidden sm:inline">Disconnect</span>
              </button>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {showConfirm && (
            <ConfirmDialog
              isOpen={showConfirm}
              onClose={() => setShowConfirm(false)}
              onConfirm={confirmDisconnect}
              deviceName={device.devicename}
              isLoading={isDisconnecting}
            />
          )}
        </AnimatePresence>
      </>
    );
  },
);

DeviceItem.displayName = 'DeviceItem';
